import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Logger } from "shared/lib/Logger"
import { getBuildingTypeIcon } from "shared/lib/building-references/getBuildingTypeIcon"
import { useEvent } from "shared/lib/useEvent"

import {
  ReferenceFilterChipListProps,
  ReferenceFilterChipListState
} from "../ReferenceFilterChipList"
import {
  OnRemoveReferenceFilter,
  ReferenceFilterChipListViewModel
} from "../ReferenceFilterChipListView"
import {
  ReferenceFilterItem,
  ReferenceFilterType
} from "../types/ReferenceFilterType"

export function useReferenceFilterChipListViewModel<
  T extends ReferenceFilterChipListState
>({
  filterState,
  productOptions,
  onChange
}: ReferenceFilterChipListProps<T>): ReferenceFilterChipListViewModel {
  const { t } = useTranslation()

  const handleRemove: OnRemoveReferenceFilter = useEvent(
    (filterType, value) => {
      const newState = { ...filterState }

      switch (filterType) {
        case ReferenceFilterType.PRODUCT:
          newState.products =
            filterState.products?.filter(p => p !== value) || []
          break
        case ReferenceFilterType.BUILDING_TYPE:
          newState.buildingTypes =
            filterState.buildingTypes?.filter(bt => bt !== value) || []
          break
        case ReferenceFilterType.ROOM_TYPE:
          newState.roomTypes =
            filterState.roomTypes?.filter(rt => rt !== value) || []
          break
        default:
          Logger.warn(
            "UnknownFilterError",
            `cannot remove filter of type: ${filterType}`
          )
          return
      }

      onChange(newState)
    }
  )

  const chips = useMemo<ReferenceFilterItem[]>(() => {
    const result: ReferenceFilterItem[] = []

    if (filterState.products?.length && productOptions) {
      filterState.products.forEach(productId => {
        const productOption = productOptions.find(
          opt => opt.value === productId
        )
        if (productOption) {
          result.push({
            key: `product-${productId}`,
            label: productOption.label,
            image: productOption.image,
            productObj: productOption.data,
            filterType: ReferenceFilterType.PRODUCT,
            value: productId
          })
        }
      })
    }

    if (filterState.buildingTypes?.length) {
      filterState.buildingTypes.forEach(buildingType => {
        result.push({
          key: `buildingType-${buildingType}`,
          label: t(`BuildingReference.BuildingType.${buildingType}`) as string,
          icon: getBuildingTypeIcon(buildingType),
          filterType: ReferenceFilterType.BUILDING_TYPE,
          value: buildingType
        })
      })
    }

    if (filterState.roomTypes?.length) {
      filterState.roomTypes.forEach(roomType => {
        result.push({
          key: `roomType-${roomType}`,
          label: t(`RoomType.${roomType}`) as string,
          filterType: ReferenceFilterType.ROOM_TYPE,
          value: roomType
        })
      })
    }

    return result
  }, [filterState, productOptions, t])

  return { chips, onRemove: handleRemove }
}
